import { useCallback, useState } from 'react';

import { deleteRequest, get, post, put } from 'helpers/api';
import { getRequestReference } from 'helpers/axios';

import { getDefaults, serializeFilters } from './filters/helpers';
import { refreshActivities } from 'bundles/apex/activities/hooks';

import type { Attachment, Task, TaskAttributes } from 'bundles/apex/tasks/types';
import type { ApiFilters } from './filters/types';

interface Pagination {
  currentPage: number;
  totalPages: number;
}

type Paths = {
  create?: string;
  index?: string;
}

// TODO: Clean up the task component so it makes its own create/update/delete calls
//       Serialization can move into the create/update calls then too, instead of happening in the modal
// TODO: Eliminate updateTask, replaceTask, addTaskAttachment, removeTaskAttachment
//       Instead, task components should manage their own internal state to keep this a simpler hook
//       that just wraps a collection
//
// Filters are required, but can start as null to allow for them to be set on mount 
//
// If external tasks are provided, an initial load will not be performed (unless there are filters)
export function useTasksState(
  { dateRangeEnabled, paginate, paths, tasks: externalTasks, tbdEnabled }: {
    dateRangeEnabled?: boolean;
    filters?: ApiFilters;
    paginate?: boolean;
    paths: Paths;
    tasks?: Task[];
    tbdEnabled?: boolean;
  }
) {
  const [filters, _setFilters] = useState<ApiFilters | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [tasks, setTasks] = useState(externalTasks || []);
  const [page, _setPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination | null>(null);

  const setFilters = useCallback((filters: ApiFilters) => {
    _setFilters(filters);
    _setPage(1);

    if (
      initialized ||
      !externalTasks ||
      JSON.stringify(filters) !== JSON.stringify(serializeFilters(getDefaults({ dateRangeEnabled, tbdEnabled })))
    ) {
      loadTasks(filters, 1);
    } else {
      setLoading(false);
    }

    setInitialized(true);
  }, [initialized]);

  const setPage = useCallback(async (page: number) => {
    _setPage(page);

    let tasks: Task[] = [];
    if (filters) {
      tasks = await loadTasks(filters, page);
    }

    return tasks;
  }, [filters]);

  const loadTasks = useCallback(async (filters: ApiFilters, page: number) => {
    setLoading(true);

    const path = paths.index || '/apex/tasks';
    const data = await getTasks({ path, filters, page: paginate ? page : null });

    setLoading(false);

    let tasks: Task[];
    if (paginate) {
      setPagination(data.meta);
      tasks = data.tasks;
    } else {
      tasks = data;
    }

    setTasks(tasks);
    return tasks;
  }, []);

  const reloadTasks = useCallback(() => {
    if (filters) {
      loadTasks(filters, page);
    }
  }, [filters, page]);

  const createTask = useCallback(async (task: TaskAttributes) => {
    const savedTask = await post(paths.create, task);
    setTasks(tasks => [savedTask, ...tasks]);
    setSelectedTask(savedTask);
    return savedTask;
  }, []);

  const updateTask = useCallback(async (task: TaskAttributes) => {
    const savedTask = await put(task.path, task);
    if (savedTask.createdActivity) {
      refreshActivities();
    }
    replaceTask(savedTask);
    return savedTask;
  }, []);

  const replaceTask = useCallback((updatedTask: Task) => {
    setTasks(tasks => tasks.map(task => task.id === updatedTask.id ? updatedTask : task));
    setSelectedTask(updatedTask);
  }, []);

  const deleteTask = useCallback(async (task: Task) => {
    const deletedTask = await deleteRequest(task.path);
    setTasks(tasks => tasks.filter(task => task.id !== deletedTask.id));
    if (selectedTask?.id === deletedTask.id) {
      setSelectedTask(null);
    }
  }, [selectedTask]);

  const addTaskAttachment = useCallback((task: Task, attachment: Attachment) => {
    replaceTask({
      ...task,
      attachments: [...task.attachments, attachment],
    });
  }, []);

  const removeTaskAttachment = useCallback((task: Task, attachment: Attachment) => {
    replaceTask({
      ...task,
      attachments: task.attachments.filter(taskAttachment => taskAttachment.id !== attachment.id),
    });
  }, []);

  return {
    selectedTask,
    setSelectedTask,
    tasks,

    setFilters,

    loading,
    setLoading, // Useful for debounced inputs to be allowed to pre-set the loading state

    pagination,
    setPage,

    addTaskAttachment,
    createTask,
    deleteTask,
    reloadTasks,
    removeTaskAttachment,
    replaceTask,
    updateTask,
  };
}

let requestReference;
async function getTasks({ path, filters, page }: { path: string, filters: ApiFilters, page: number | null }) {
  if (requestReference) {
    requestReference.cancel();
  }
  requestReference = getRequestReference();

  return get(path, {
    cancelToken: requestReference.token,
    params: { ...filters, include: ['parent'], page, sortBy: filters.tbd ? null : 'deadline' },
  });
}
